import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Tag, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromoLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [promoData, setPromoData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    handlePromo();
  }, [code]);

  const handlePromo = async (retries = 3) => {
    try {
      // Track scan
      await axios.post(`${API}/promo-codes/${code}/scan`);

      // Try to apply
      const res = await axios.post(`${API}/promo-codes/apply`, { code: code });
      setPromoData(res.data);
      setStatus("success");

      // Redirect after delay
      setTimeout(() => {
        navigate(`/?promo_code=${code}&promo_discount=${res.data.discount_percent}`);
      }, 3000);
    } catch (e) {
      // Retry if backend might still be waking up (network error or 502/503)
      const status_code = e.response?.status;
      const isServerWaking = !e.response || status_code === 502 || status_code === 503;
      if (isServerWaking && retries > 0) {
        setStatus("waking");
        await new Promise(r => setTimeout(r, 2000));
        return handlePromo(retries - 1);
      }

      setErrorMsg(e.response?.data?.detail || "Invalid or expired promo code");
      setStatus("error");

      // Still redirect to main page after delay
      setTimeout(() => {
        navigate("/");
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#FAFAFA]" data-testid="promo-landing-page">
      <div className="brutal-card p-12 text-center max-w-md">
        {status === "loading" && (
          <div data-testid="promo-loading">
            <Loader2 size={48} className="mx-auto mb-4 animate-spin text-[#FF3366]" strokeWidth={2.5} />
            <h2 className="font-heading text-xl font-bold uppercase">Applying Promo Code...</h2>
            <p className="text-zinc-500 text-sm mt-2">Please wait while we apply your discount.</p>
          </div>
        )}

        {status === "waking" && (
          <div data-testid="promo-waking">
            <Loader2 size={48} className="mx-auto mb-4 animate-spin text-[#FFD600]" strokeWidth={2.5} />
            <h2 className="font-heading text-xl font-bold uppercase">Warming Up...</h2>
            <p className="text-zinc-500 text-sm mt-2">Our servers are waking up. Hang tight, this only takes a moment!</p>
          </div>
        )}

        {status === "success" && promoData && (
          <div data-testid="promo-success">
            <div className="w-16 h-16 bg-[#FFD600] border-2 border-black flex items-center justify-center mx-auto mb-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <Tag size={32} strokeWidth={2.5} />
            </div>
            <h2 className="font-heading text-2xl font-black uppercase mb-2">
              {promoData.discount_percent}% Off!
            </h2>
            <p className="text-zinc-500 mb-4">Your discount has been applied. Redirecting to shop...</p>
            <div className="inline-block bg-[#00E5FF] border-2 border-black px-4 py-2 font-mono font-bold text-sm tracking-wider">
              Code: {promoData.code}
            </div>
          </div>
        )}

        {status === "error" && (
          <div data-testid="promo-error">
            <div className="w-16 h-16 bg-[#FFD600] border-2 border-black flex items-center justify-center mx-auto mb-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <AlertTriangle size={32} strokeWidth={2.5} />
            </div>
            <h2 className="font-heading text-xl font-bold uppercase mb-2">{errorMsg}</h2>
            <p className="text-zinc-500">Redirecting to shop...</p>
          </div>
        )}
      </div>
    </div>
  );
}
